import forEach from "lodash/fp/forEach";
import produce from "immer";

// Actions
export const VERIFY = "progress/VERIFY";
export const INIT = "progress/INIT";
export const RECORD = "progress/RECORD";
export const FINISH = "progress/FINISH";
export const ACTIVITY_RECORD = "progress/activity/RECORD";
export const ACTIVITY_FINISH = "progress/activity/FINISH";
export const GROUPS_INIT = "progress/groups/INIT";
export const GROUP_RECORD = "progress/group/RECORD";
export const GROUP_FINISH = "progress/group/FINISH";

const initialState = {
  // User has clicked the button to start module
  verified: false,
  initialise: null,
  modules: {},
  activities: {},
  groups: {},
};

// Reducer
export default function reducer(state = initialState, action = {}) {
  return produce(state, (draft) => {
    switch (action.type) {
      case VERIFY: {
        draft.verified = true;
        break;
      }

      case INIT: {
        const { children } = action;

        forEach(({ id, title, elementalArea }) => {
          // init empty state
          if (draft.modules[id] === undefined) {
            draft.modules[id] = { title, step: 0, finished: false };

            return;
          }

          // if already  initialised, make sure step is within total possible amount
          const module = draft.modules[id];
          const {
            elements: {
              pageInfo: { totalCount },
            },
          } = elementalArea;
          const max = totalCount + 1;
          if (max < module.step) {
            // setting to finish screen if a step has been removed
            module.step = max;
          }

          // make sure we have a finished state
          if (module.finished === undefined) {
            module.finished = false;
          }
        })(children);

        break;
      }

      case RECORD: {
        const { id, step } = action.props;

        if (!id) {
          throw new Error("must provide an ID to progress action");
        }
        const module = draft.modules[id];

        // never remove progress
        if (step <= module.step) {
          break;
        }

        module.step = step;

        break;
      }

      case FINISH: {
        const { id } = action;
        if (!id) {
          throw new Error("must provide an ID to progress action");
        }

        // finished is a special state that indicates progress is no longer displayed
        draft.modules[id].finished = true;
        break;
      }

      case ACTIVITY_RECORD: {
        const { id, step } = action.props;

        if (!id || !Number.isInteger(step)) {
          throw new Error(
            "must provide an ID and step to activity progress action"
          );
        }

        // initialize if missing data
        if (!draft.activities) {
          draft.activities = { [id]: { step, finished: false } };
          break;
        }

        if (!draft.activities?.[id]) {
          draft.activities[id] = { step, finished: false };
          break;
        }

        draft.activities[id].step = step;

        break;
      }

      case ACTIVITY_FINISH: {
        const { id, finished } = action.props;

        // initialize if missing data
        if (!draft.activities) {
          draft.activities = { [id]: { step: 0, finished } };
          break;
        }

        if (!draft.activities[id]) {
          draft.activities[id] = { finished, step: 0 };
        }

        draft.activities[id].finished = finished;

        break;
      }

      case GROUPS_INIT: {
        const { groups } = action.props;
        // initialize if missing data
        if (!draft.groups) {
          draft.groups = {};
        }

        groups.forEach((node) => {
          const { id } = node;
          // init empty state
          if (draft.groups[id] === undefined) {
            draft.groups[id] = { step: 0, finished: false };

            return;
          }

          // // if already  initialised, make sure step is within total possible amount
          const group = draft.groups[id];
          const {
            elements: {
              elements: {
                pageInfo: { totalCount },
              },
            },
          } = node;
          const max = totalCount + 1;
          if (max <= group.step) {
            // setting to finished if steps are too high (eg a step has been removed)
            group.step = max;
            group.finished = true;
          }

          // make sure we have a finished state
          if (group.finished === undefined) {
            group.finished = false;
          }
        });

        break;
      }

      case GROUP_RECORD: {
        const { id, step } = action.props;

        if (!id || !Number.isInteger(step)) {
          throw new Error(
            "must provide an ID and step to activity progress action"
          );
        }

        // initialize if missing data
        if (!draft.groups) {
          draft.groups = { [id]: { step, finished: false } };
          break;
        }

        if (!draft.groups?.[id]) {
          draft.groups[id] = { step, finished: false };
          break;
        }

        draft.groups[id].step = step;

        break;
      }

      case GROUP_FINISH: {
        const { id, finished } = action.props;
        draft.groups[id].finished = finished;

        break;
      }
    }
  });
}

// Action Creators
export function setVerified() {
  return { type: VERIFY };
}

/**
 *
 * @param {Object} props
 * @param {string|number} props.id - module id
 * @param {number} props.step - progress step
 */
export function setModuleProgress(props) {
  return { type: RECORD, props };
}

export function init(children) {
  return { type: INIT, children };
}

/**
 *
 * @param {string|number} id
 */
export function setFinished(id) {
  return { type: FINISH, id };
}

/**
 *
 * @param {Object} props
 * @param {string|number} props.id - activity id
 * @param {number} props.step - progress step
 */
export function setActivityProgress(props) {
  return { type: ACTIVITY_RECORD, props };
}

/**
 *
 * @param {Object} props
 * @param {string|number} props.id - activity id
 * @param {bool} props.finished - finished flag
 */
export function setActivityFinished(props) {
  return { type: ACTIVITY_FINISH, props };
}

export function initGroups(props) {
  return { type: GROUPS_INIT, props };
}

/**
 *
 * @param {Object} props
 * @param {string|number} props.id - group id
 * @param {number} props.step - progress step
 */
export function setGroupProgress(props) {
  return { type: GROUP_RECORD, props };
}

/**
 *
 * @param {Object} props
 * @param {string|number} props.id - group id
 * @param {bool} props.finished - finished flag
 */
export function setGroupFinished(props) {
  return { type: GROUP_FINISH, props };
}

// selector
export function selectProgress(state) {
  return state.progress;
}
